import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ResponseCatalog, RequestParams } from 'Models'
import { SimpleAction } from 'Types/action'
import { Callback } from 'Src/types/utilTypes'
import {
	OrderModel,
	OrdersListView,
	ReportType,
	OrderBuildRequest,
	OrderStepType,
	ContactDetails,
	CommentModel,
	ReserveAccountModel,
	RecognizePassportFieldsModel,
	AGREEMENT_TYPE,
} from 'Pages/orders_design'
import { FileWithPreview } from 'LibNeo'
import { ProductSlugs } from 'Constants/ProductSlugs'
import { ModalOptions } from 'Modules'
import { ProductModel } from 'Pages/orders'

export type OrdersState = {
	items: ResponseCatalog<OrderModel>
	requestParams: RequestParams
	total: number
	ordersListView: OrdersListView
	acceptBlocked: boolean
	currentOrder: OrderModel
	step: OrderStepType
	totalIncompleted: number
	blockExpiresSoon: number
	totalBackChecking: number
	totalPartnerRequests: number
	clientINN: string
	paperAgreement: boolean
	orderFormValues: string
	recognizeData: Map<number, RecognizePassportFieldsModel>
	recognizeLoading: boolean
	consentShow: boolean
	selectedRecommendations: {}
	selectedProducts: ProductModel[]
	selectedOnlineProducts: Set<number>
	blacklistedInn: boolean | null
	otkritieBankCities: string[]
	totalTasks: number | null
	totalEntriesLoader: boolean
	isElectronicConsent: boolean
	agreementType: AGREEMENT_TYPE
	phoneAgreement: string
	agreementStatus: string
	totalRejectedReservation: number | null
}

export const ordersInitialState: OrdersState = {
	items: new ResponseCatalog<OrderModel>(),
	total: null,
	totalIncompleted: null,
	totalBackChecking: null,
	totalPartnerRequests: null,
	requestParams: new RequestParams(1, 20),
	ordersListView: OrdersListView.FULL,
	currentOrder: null,
	acceptBlocked: false,
	clientINN: undefined,
	paperAgreement: false,
	orderFormValues: null,
	step: OrderStepType.PRE_FILLING,
	otkritieBankCities: [],
	totalTasks: null,
	recognizeData: new Map(),
	recognizeLoading: false,
	consentShow: false,
	selectedRecommendations: null,
	selectedProducts: [],
	selectedOnlineProducts: new Set<number>(),
	blacklistedInn: null,
	blockExpiresSoon: null,
	totalEntriesLoader: false,
	agreementStatus: undefined,
	isElectronicConsent: true,
	agreementType: AGREEMENT_TYPE.SMS,
	phoneAgreement: undefined,
	totalRejectedReservation: null,
}

export namespace ordersNS {
	export type getAll = PayloadAction<{
		params: RequestParams
		clear?: boolean
		callback?: Callback
	}>
	export type setAll = PayloadAction<ResponseCatalog<OrderModel>>
	export type setParams = PayloadAction<RequestParams>
	export type getTotal = PayloadAction<{
		params: RequestParams
	}>
	export type setTotal = PayloadAction<number>

	export type cancelDzoStatus = PayloadAction<{ event_id: number }>

	export type paoSberbankStatus = PayloadAction<{ event_id: number }>

	export type crmStatus = PayloadAction<{ event_id: number }>

	export type setOrdersListView = PayloadAction<OrdersListView>

	export type sendOrdersReport = PayloadAction<{ type: ReportType; params: RequestParams }>
	export type sendPaymentStatuses = PayloadAction<FileWithPreview | FileWithPreview[]>
	export type sendCorusStatuses = PayloadAction<FileWithPreview | FileWithPreview[]>
	export type sendCRMStatuses = PayloadAction<FileWithPreview | FileWithPreview[]>

	export type buildOrder = PayloadAction<OrderBuildRequest>

	export type setAcceptBlocked = PayloadAction<boolean>

	export type getOrder = PayloadAction<{
		id: number
		updateList?: boolean
	}>
	export type setOrder = PayloadAction<OrderModel>

	export type setStep = PayloadAction<OrderStepType>

	export type createOrder = PayloadAction<{
		order: OrderModel
		errorCallback?: Callback
	}>

	export type getTotalIncompleted = PayloadAction
	export type setTotalIncompleted = PayloadAction<number>

	export type getBlockExpiresSoon = PayloadAction
	export type setBlockExpiresSoon = PayloadAction<number>

	export type getTotalUnblock = PayloadAction
	export type setTotalUnblock = PayloadAction<number>

	export type getTotalPartnerRequests = PayloadAction

	export type setTotalPartnerRequest = PayloadAction<number>

	export type deleteOrder = PayloadAction<number>

	export type getContactDetailsCurrentOrder = PayloadAction<{ id: number }>
	export type setContactDetailsCurrentOrder = PayloadAction<{
		id: number
		contactDetails: ContactDetails
	}>

	export type resendSms = PayloadAction<{ id: number; productSlug: ProductSlugs }>

	export type resendEmail = PayloadAction<{ id: number; productSlug: ProductSlugs }>

	export type setClientINN = PayloadAction<string>

	export type setPaperAgreement = PayloadAction<boolean>
	export type sendComment = PayloadAction<{ id: number; text: string; callback: Callback }>

	export type addComment = PayloadAction<CommentModel>

	export type recognizePassport = PayloadAction<{ file: Blob; order_id: string }>
	export type setRecognizedData = PayloadAction<{
		recognition_data: RecognizePassportFieldsModel
		order_id: number
	}>
	export type setRecognizeLoading = PayloadAction<boolean>
	export type setConsentShow = PayloadAction<boolean>
	export type setSelectedRecommendation = PayloadAction<{ [key: string]: number }>
	export type setSelectedProducts = PayloadAction<ProductModel[]>
	export type setSelectedOnlineProductsIds = PayloadAction<Set<number>>
	export type setBlacklistedInn = PayloadAction<boolean | null>
	export type sendClickThrough = PayloadAction
	export type getOtkritieBankCities = PayloadAction
	export type setOtkritieBankCities = PayloadAction<string[]>

	export type getTotalTasks = PayloadAction<SimpleAction>
	export type setTotalTasks = PayloadAction<number>
	export type setReserveAccountInOrder = PayloadAction<ReserveAccountModel>
	export type getIntegrationOrder = PayloadAction<{ id: number; optionsModal: ModalOptions }>
	export type getOrderAfterRedirect = PayloadAction<number>
	export type sendFileForBackToChecking = PayloadAction<{
		unBlockRequestId: number
		files_attributes: File[]
	}>
	export type setOrderFormValues = PayloadAction<string>
	export type setAgreementStatus = PayloadAction<string>
	export type setIsElectronicAgreement = PayloadAction<boolean>
	export type setAgreementTypeValue = PayloadAction<AGREEMENT_TYPE>
	export type setSavedPhone = PayloadAction<string>
	export type setTotalEntriesLoader = PayloadAction<boolean>

	export type getTotalRejectedReservation = PayloadAction
	export type setTotalRejectedReservation = PayloadAction<number>
	export type bankAccountStatuses = PayloadAction<FileWithPreview | FileWithPreview[]>
}

export const ordersSlice = createSlice({
	name: 'orders_design',
	initialState: ordersInitialState,
	reducers: {
		getAll: (state, action: ordersNS.getAll) => {},
		setAll: (state, action: ordersNS.setAll) => {
			state.items = action.payload
		},
		setParams: (state, action: ordersNS.setParams) => {
			state.requestParams = action.payload
		},
		clearAll: (state) => {
			state.items = new ResponseCatalog<OrderModel>()
		},
		getTotal: (state, action: ordersNS.getTotal) => {},
		setTotal: (state, action: ordersNS.setTotal) => {
			state.total = action.payload
		},
		cancelDzoStatus: (state, action: ordersNS.cancelDzoStatus) => {},
		paoSberbankStatus: (state, action: ordersNS.paoSberbankStatus) => {},
		crmStatus: (state, action: ordersNS.crmStatus) => {},
		setOrdersListView: (state, action: ordersNS.setOrdersListView) => {
			state.ordersListView = action.payload
		},
		sendOrdersReport: (state, action: ordersNS.sendOrdersReport) => {},
		sendPaymentStatuses: (state, action: ordersNS.sendPaymentStatuses) => {},
		sendCorusStatuses: (state, action: ordersNS.sendCorusStatuses) => {},
		sendCRMStatuses: (state, action: ordersNS.sendCRMStatuses) => {},
		buildOrder: (state, action: ordersNS.buildOrder) => {},
		setAcceptBlocked: (state, action: ordersNS.setAcceptBlocked) => {
			state.acceptBlocked = true
		},
		clearAcceptBlocked: (state) => {
			state.acceptBlocked = false
		},
		getOrder: (state, action: ordersNS.getOrder) => {},
		setOrder: (state, action: ordersNS.setOrder) => {
			state.currentOrder = action.payload
		},
		clearOrder: (state) => {
			state.currentOrder = null
		},
		setStep: (state, action: ordersNS.setStep) => {
			state.step = action.payload
		},
		createOrder: (state, action: ordersNS.createOrder) => {},
		getTotalIncompleted: (state, action: ordersNS.getTotalIncompleted) => {},
		setTotalIncompleted: (state, action: ordersNS.setTotalIncompleted) => {
			state.totalIncompleted = action.payload
		},
		getBlockExpiresSoon: (state, action: ordersNS.getBlockExpiresSoon) => {},
		setBlockExpiresSoon: (state, action: ordersNS.setBlockExpiresSoon) => {
			state.blockExpiresSoon = action.payload
		},
		getTotalUnblock: (state, action: ordersNS.getTotalUnblock) => {},
		setTotalUnblock: (state, action: ordersNS.setTotalUnblock) => {
			state.totalBackChecking = action.payload
		},
		getTotalPartnerRequests: (state, action: ordersNS.getTotalPartnerRequests) => {},
		setTotalPartnerRequest: (state, action: ordersNS.setTotalPartnerRequest) => {
			state.totalPartnerRequests = action.payload
		},
		deleteOrder: (state, action: ordersNS.deleteOrder) => {},

		getContactDetailsCurrentOrder: (state, action: ordersNS.getContactDetailsCurrentOrder) => {},
		setContactDetailsCurrentOrder: (state, action: ordersNS.setContactDetailsCurrentOrder) => {
			state.currentOrder =
				state.currentOrder.id === action.payload.id
					? ({
							...state.currentOrder,
							contact_details: action.payload.contactDetails,
					  } as OrderModel)
					: state.currentOrder
		},

		resendSms: (state, action: ordersNS.resendSms) => {},
		resendEmail: (state, action: ordersNS.resendEmail) => {},
		setClientINN: (state, action: ordersNS.setClientINN) => {
			state.clientINN = action.payload
		},
		clearClientINN: (state) => {
			state.clientINN = undefined
		},
		setPaperAgreement: (state, action: ordersNS.setPaperAgreement) => {
			state.paperAgreement = action.payload
		},
		sendComment: (state, action: ordersNS.sendComment) => {},

		addComment: (state, action: ordersNS.addComment) => {
			state.currentOrder =
				state.currentOrder.id === action.payload.order_id
					? ({
							...state.currentOrder,
							comments: [...state.currentOrder.comments, action.payload],
					  } as OrderModel)
					: state.currentOrder
		},

		recognizePassport: (state, action: ordersNS.recognizePassport) => {},
		setRecognizedData: (state, action: ordersNS.setRecognizedData) => {
			const { recognition_data, order_id } = action.payload
			const newState = { ...state, recognizeData: new Map(state.recognizeData) }
			newState.recognizeData.set(order_id, recognition_data)
			return newState
		},
		clearRecognizedData: (state) => {
			state.recognizeData = new Map()
		},
		setRecognizeLoading: (state, action: ordersNS.setRecognizeLoading) => {
			state.recognizeLoading = action.payload
		},
		setConsentShow: (state, action: ordersNS.setConsentShow) => {
			state.consentShow = action.payload
		},

		setSelectedRecommendation: (state, action: ordersNS.setSelectedRecommendation) => {
			state.selectedRecommendations = action.payload
		},
		setSelectedProducts: (state, action: ordersNS.setSelectedProducts) => {
			state.selectedProducts = action.payload
		},
		clearSelectedProducts: (state) => {
			state.selectedProducts = []
		},
		setSelectedOnlineProductsIds: (state, action: ordersNS.setSelectedOnlineProductsIds) => {
			state.selectedOnlineProducts = action.payload
		},
		clearSelectedOnlineProducts: (state) => {
			state.selectedOnlineProducts = new Set<number>()
		},
		setBlacklistedInn: (state, action: ordersNS.setBlacklistedInn) => {
			state.blacklistedInn = action.payload
		},
		sendClickThrough: (state, action: ordersNS.sendClickThrough) => {},
		getOtkritieBankCities: (state, action: ordersNS.getOtkritieBankCities) => {},
		setOtkritieBankCities: (state, action: ordersNS.setOtkritieBankCities) => {
			state.otkritieBankCities = action.payload
		},
		getTotalTasks: (state, action: ordersNS.getTotalTasks) => {},
		setTotalTasks: (state, action: ordersNS.setTotalTasks) => {
			state.totalTasks = action.payload
		},
		setOrderFormValues: (state, action: ordersNS.setOrderFormValues) => {
			state.orderFormValues = action.payload
		},
		setReserveAccountInOrder: (state, action: ordersNS.setReserveAccountInOrder) => {
			state.currentOrder = {
				...state.currentOrder,
				reserve_account: action.payload,
			} as OrderModel
		},
		getIntegrationOrder: (state, action: ordersNS.getIntegrationOrder) => {},
		getOrderAfterRedirect: (state, action: ordersNS.getOrderAfterRedirect) => {},
		sendFileForBackToChecking: (state, action: ordersNS.sendFileForBackToChecking) => {},
		setAgreementStatus: (state, action: ordersNS.setAgreementStatus) => {
			state.agreementStatus = action.payload
		},
		setIsElectronicAgreement: (state, action: ordersNS.setIsElectronicAgreement) => {
			state.isElectronicConsent = action.payload
		},
		setAgreementTypeValue: (state, action: ordersNS.setAgreementTypeValue) => {
			state.agreementType = action.payload
		},
		setSavedPhone: (state, action: ordersNS.setSavedPhone) => {
			state.phoneAgreement = action.payload
		},
		setTotalEntriesLoader: (state, action: ordersNS.setTotalEntriesLoader) => {
			state.totalEntriesLoader = action.payload
		},
		getTotalRejectedReservation: (state, action: ordersNS.getTotalRejectedReservation) => {},
		setTotalRejectedReservation: (state, action: ordersNS.setTotalRejectedReservation) => {
			state.totalRejectedReservation = action.payload
		},
		bankAccountStatuses: (state, action: ordersNS.bankAccountStatuses) => {},
	},
})
